import React, { useState } from "react";
import AdminLayout from "./AdminDashboardLayout";
import Button from "../../utils/Button";
import toast from "react-hot-toast";
import { useApiQuery } from "../../hooks/useApiQuery";
import { request } from "../../utils/api";

interface BillingSummary {
  activePartners: number;
  totalBilled: number;
  totalEmails: number;
  totalSms: number;
}

interface Partner {
  emailCost: number;
  emailCount: number;
  partnerId: number;
  partnerIdentifier: string;
  partnerName: string;
  partnerStatus: "ACTIVE" | "INACTIVE";
  smsCost: number;
  smsCount: number;
  subscriptionStatus: "ACTIVE" | "INACTIVE";
  subscriptionTier: string;
  totalBill: number;
}

interface PartnerDetails {
  billingCycle: string;
  emailCost: number;
  gracePeriodLeft: string;
  lastPaymentAmount: number | null;
  lastPaymentDate: string | null;
  monthlyEmails: number;
  monthlySms: number;
  nextBillingDate: string | null;
  partnerIdentifier: string;
  smsCost: number;
  stripeCustomerId: string;
  subscriptionStatus: "ACTIVE" | "INACTIVE";
  subscriptionTier: string;
  totalCurrentBill: number;
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

const BillingUsage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [selectedPartner, setSelectedPartner] = useState<PartnerDetails | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Fetch billing summary
  const { data: billingSummaryData, isLoading: summaryLoading } = useApiQuery<ApiResponse<BillingSummary>>({
    queryKey: ['billing-summary'],
    request: {
      method: 'GET',
      url: '/admin/billing/summary',
    },
  });

  // Fetch partners data
  const { data: partnersData, isLoading: partnersLoading } = useApiQuery<ApiResponse<Partner[]>>({
    queryKey: ['billing-partners'],
    request: {
      method: 'GET',
      url: '/admin/billing/partners',
    },
  });

  const billingSummary = billingSummaryData?.data;
  const partners = partnersData?.data || [];
  const loading = summaryLoading || partnersLoading;

  // Fetch partner details
  const fetchPartnerDetails = async (partnerId: number) => {
    try {
      const response = await request<ApiResponse<PartnerDetails>>({
        method: 'GET',
        url: `/admin/billing/partners/${partnerId}`,
      });
      if (response.success) {
        setSelectedPartner(response.data);
        setIsDetailsModalOpen(true);
      }
    } catch (error) {
      toast.error("Failed to fetch partner details");
      console.error("Error fetching partner details:", error);
    }
  };

  const filteredData = partners.filter(item => {
    const matchesSearch = item.partnerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.partnerIdentifier.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.subscriptionTier.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = selectedFilter === "all" || 
                         (selectedFilter === "active" && item.subscriptionStatus === "ACTIVE") ||
                         (selectedFilter === "inactive" && item.subscriptionStatus === "INACTIVE");
    
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full border bg-green-100 text-green-800 border-green-200">
            Active
          </span>
        );
      case "INACTIVE":
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full border bg-red-100 text-red-800 border-red-200">
            Inactive
          </span>
        );
      default:
        return null;
    }
  };

  const openDetails = (partner: Partner) => {
    fetchPartnerDetails(partner.partnerId);
  };

  return (
    <AdminLayout role="SYSTEM_ADMIN">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Billing Usage and Status</h1>
            <p className="mt-1 text-sm text-gray-600">
              Monitor partner billing status, webhooks, and messaging costs
            </p>
          </div>
        </div>

                {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Active Partners</p>
                <p className="text-lg font-semibold text-gray-900">
                  {loading ? "..." : (billingSummary?.activePartners || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Total SMS This Month</p>
                <p className="text-lg font-semibold text-gray-900">
                  {loading ? "..." : (billingSummary?.totalSms || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Total Emails This Month</p>
                <p className="text-lg font-semibold text-gray-900">
                  {loading ? "..." : (billingSummary?.totalEmails || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Total Billed This Month</p>
                <p className="text-lg font-semibold text-gray-900">
                  ${loading ? "..." : (billingSummary?.totalBilled || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>


        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by partner name, ID, action, or details..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <div>
              <select
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Billing Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Partner
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subscription Tier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monthly SMS
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monthly Emails
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SMS Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Bill
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      Loading billing data...
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      {searchTerm ? "No billing records found matching your search." : "No billing records found."}
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item) => (
                    <tr 
                      key={item.partnerId} 
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => openDetails(item)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.partnerName}</div>
                          <div className="text-sm text-gray-500">{item.partnerIdentifier}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.subscriptionTier}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.smsCount.toLocaleString()}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.emailCount.toLocaleString()}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">${item.smsCost.toFixed(2)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">${item.emailCost.toFixed(2)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">${item.totalBill.toFixed(2)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(item.subscriptionStatus)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>


        {/* Billing Details Modal */}
        {isDetailsModalOpen && selectedPartner && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => setIsDetailsModalOpen(false)}
              ></div>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        Billing Details - {selectedPartner.partnerIdentifier}
                      </h3>
                      
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {/* Stripe Customer ID */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Stripe Customer ID</label>
                          <p className="mt-1 text-sm text-gray-900 font-mono">{selectedPartner.stripeCustomerId}</p>
                        </div>

                        {/* Subscription Tier */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Subscription Tier</label>
                          <p className="mt-1 text-sm text-gray-900 font-semibold">{selectedPartner.subscriptionTier}</p>
                        </div>

                        {/* Status */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Billing Status</label>
                          <div className="mt-1">
                            {getStatusBadge(selectedPartner.subscriptionStatus)}
                          </div>
                        </div>

                        {/* Last Payment Amount */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Last Payment Amount</label>
                          <p className="mt-1 text-sm text-gray-900">
                            ${selectedPartner.lastPaymentAmount ? selectedPartner.lastPaymentAmount.toFixed(2) : 'N/A'}
                          </p>
                        </div>

                        {/* Last Payment Date */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Last Payment Date</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {selectedPartner.lastPaymentDate ? new Date(selectedPartner.lastPaymentDate).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            }) : 'N/A'}
                          </p>
                        </div>

                        {/* Grace Period */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Grace Period Left</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {selectedPartner.gracePeriodLeft}
                          </p>
                        </div>

                        {/* Next Billing Date */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Next Billing Date</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {selectedPartner.nextBillingDate ? new Date(selectedPartner.nextBillingDate).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            }) : 'N/A'}
                          </p>
                        </div>

                        {/* Billing Cycle */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Billing Cycle</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedPartner.billingCycle}</p>
                        </div>

                        {/* Partner ID */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Partner ID</label>
                          <p className="mt-1 text-sm text-gray-900 font-mono">{selectedPartner.partnerIdentifier}</p>
                        </div>
                      </div>

                      {/* Usage Summary */}
                      <div className="mt-6 border-t border-gray-200 pt-6">
                        <h4 className="text-md font-medium text-gray-900 mb-4">Current Month Usage</h4>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm font-medium text-gray-600">Monthly SMS</p>
                            <p className="text-lg font-semibold text-gray-900">{selectedPartner.monthlySms.toLocaleString()}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm font-medium text-gray-600">Monthly Emails</p>
                            <p className="text-lg font-semibold text-gray-900">{selectedPartner.monthlyEmails.toLocaleString()}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm font-medium text-gray-600">SMS Cost</p>
                            <p className="text-lg font-semibold text-gray-900">${selectedPartner.smsCost.toFixed(2)}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm font-medium text-gray-600">Email Cost</p>
                            <p className="text-lg font-semibold text-gray-900">${selectedPartner.emailCost.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>

                      {/* Total Bill */}
                      <div className="mt-6 border-t border-gray-200 pt-6">
                        <div className="flex justify-between items-center">
                          <h4 className="text-md font-medium text-gray-900">Total Current Bill</h4>
                          <p className="text-2xl font-bold text-gray-900">${selectedPartner.totalCurrentBill.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <Button
                    onClick={() => setIsDetailsModalOpen(false)}
                    variant="primary"
                    label="Close"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default BillingUsage;
